import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, Youtube, Send, Calendar, Clock, ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1cfa97e1-e5a1-4141-a583-88431abcdf42/artifacts/4wtpibus_opad_logo-01.jpg";
const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Partners & Alliances. `logo` = self-hosted image; `mono`/`brand` = branded monogram fallback.
const partners = [
  { name: 'UN ECOSOC', tag: 'Consultative Status', logo: '/partner-logos/un.png', mono: 'UN', brand: '#5B92E5' },
  { name: 'EU Transparency Register', tag: 'ID 026269897821-30', logo: '/partner-logos/eu.png', mono: 'EU', brand: '#003399' },
  { name: 'UN Global Compact', tag: 'Member', logo: null, mono: 'UNGC', brand: '#5B92E5' },
  { name: 'UNFCCC', tag: 'Climate Engagement', logo: null, mono: 'UNFCCC', brand: '#1E5C97' },
  { name: 'UNDP', tag: 'Collaboration', logo: null, mono: 'UNDP', brand: '#0468B1' },
  { name: 'UNHCR', tag: 'Collaboration', logo: null, mono: 'UNHCR', brand: '#0072BC' },
  { name: 'OHCHR', tag: 'Collaboration', logo: null, mono: 'OHCHR', brand: '#0072BC' },
  { name: 'UNODC', tag: 'Collaboration', logo: null, mono: 'UNODC', brand: '#003F87' },
  { name: 'FAO', tag: 'Collaboration', logo: null, mono: 'FAO', brand: '#0F8A4E' },
  { name: 'Adaptation Fund', tag: 'Stakeholder', logo: null, mono: 'AF', brand: '#3CAB4F' },
  { name: 'Global Nutrition Cluster', tag: 'Member', logo: null, mono: 'GNC', brand: '#E07B00' },
  { name: 'EU CERV Programme', tag: 'Project Partner', logo: '/partner-logos/eu.png', mono: 'CERV', brand: '#003399' },
  { name: 'Harbour Education', tag: 'Academic Partner', logo: '/partner-logos/gb.png', mono: 'HE', brand: '#7BC4E2' },
  { name: 'OPAD Finland', tag: 'Network', logo: '/partner-logos/fi.png', mono: 'OPAD', brand: '#003580' },
  { name: 'C.E.P.H.I.', tag: 'NGO Partner · Italy', logo: '/partner-logos/it.png', mono: 'CEPHI', brand: '#008C45' },
  { name: 'Vrenza', tag: 'NGO Partner · Spain', logo: '/partner-logos/es.png', mono: 'AXV', brand: '#AA151B' },
  { name: 'Buarcos', tag: 'Municipal · Portugal', logo: '/partner-logos/pt.png', mono: 'PT', brand: '#006600' },
  { name: 'Héhalom', tag: 'Municipal · Hungary', logo: '/partner-logos/hu.png', mono: 'HU', brand: '#436F4D' },
  { name: 'Safi', tag: 'Municipal · Malta', logo: '/partner-logos/mt.png', mono: 'MT', brand: '#CF142B' },
  { name: 'Rezekne', tag: 'Municipal · Latvia', logo: '/partner-logos/lv.png', mono: 'LV', brand: '#9E3039' },
];

// Upcoming events (in sync with EventsPage)
const upcomingEvents = [
  {
    title: 'EU CERV — EcoActive Citizens',
    location: 'Buarcos, Portugal',
    date: '25–28 February 2026',
    tag: 'EU Project',
    color: '#003399',
  },
  {
    title: 'EU CERV — Circular Economy Phase',
    location: 'Santiago de Compostela, Spain',
    date: '1–4 March 2026',
    tag: 'EU Project',
    color: '#7BC4E2',
  },
  {
    title: 'OPAD Global Strategy Meeting 2026',
    location: 'Stockholm, Sweden',
    date: '15–17 March 2026',
    tag: 'Internal',
    color: '#4A7C59',
  },
  {
    title: 'Partnership Development Workshop',
    location: 'Virtual (Zoom)',
    date: '5 April 2026',
    tag: 'Workshop',
    color: '#E5B75F',
  },
  {
    title: 'Youth Empowerment Forum',
    location: 'Lagos, Nigeria',
    date: '20–22 April 2026',
    tag: 'Forum',
    color: '#E08B7A',
  },
  {
    title: 'High-Level Political Forum on SDGs',
    location: 'New York, USA',
    date: 'July 2026',
    tag: 'UN HLPF',
    color: '#7BC4E2',
  },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');
  const [eventIndex, setEventIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setEventIndex((i) => (i + 1) % upcomingEvents.length), 5000);
    return () => clearInterval(t);
  }, []);

  const visibleCount = 3;
  const visibleEvents = Array.from({ length: visibleCount }, (_, k) => upcomingEvents[(eventIndex + k) % upcomingEvents.length]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    try {
      await axios.post(`${API}/newsletter`, { email });
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus(''), 3000);
    } catch (error) {
      if (error.response?.status === 400) {
        setSubscribeStatus('already');
      } else {
        setSubscribeStatus('error');
      }
      setTimeout(() => setSubscribeStatus(''), 3000);
    }
  };

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Partners & Upcoming Events Slider Band */}
      <div className="bg-gradient-to-br from-slate-50 to-white text-slate-900 border-b border-slate-200" data-testid="footer-slider-band">
        {/* Partners Marquee */}
        <div className="container-custom pt-10 pb-6">
          <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
            <div>
              <span className="inline-block bg-[#4A7C59]/10 text-[#4A7C59] text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-1.5">
                Trust & Network
              </span>
              <h3 className="text-xl md:text-2xl font-bold font-['Manrope'] text-slate-900">
                Our Partners & Alliances
              </h3>
            </div>
            <Link to="/about#partners" className="text-sm font-semibold text-[#4A7C59] hover:text-[#3A6B49] flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="relative overflow-hidden group" data-testid="partners-marquee">
          {/* Edge fades */}
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-slate-50 to-transparent z-10" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-white to-transparent z-10" />

          <div className="flex w-max animate-marquee group-hover:[animation-play-state:paused] py-2">
            {[...partners, ...partners].map((p, i) => (
              <div
                key={`${p.name}-${i}`}
                className="flex items-center gap-3 mx-3 px-5 py-3 bg-white rounded-2xl shadow-sm border border-slate-100 min-w-[230px] hover:shadow-md hover:-translate-y-0.5 transition"
              >
                {p.logo ? (
                  <img
                    src={p.logo}
                    alt={`${p.name} logo`}
                    className="h-9 w-12 object-contain object-center flex-shrink-0 rounded-sm"
                    loading="lazy"
                  />
                ) : (
                  <span
                    className="h-9 w-12 flex items-center justify-center font-extrabold text-white text-[10px] tracking-tight rounded-md flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: p.brand || '#475569' }}
                  >
                    {p.mono}
                  </span>
                )}
                <div className="flex flex-col leading-tight">
                  <span className="font-semibold text-slate-900 text-sm whitespace-nowrap">{p.name}</span>
                  <span className="text-[11px] text-slate-500 whitespace-nowrap">{p.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Events Carousel */}
        <div className="container-custom pt-8 pb-10">
          <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
            <div>
              <span className="inline-block bg-[#E08B7A]/10 text-[#E08B7A] text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-1.5">
                What's Next
              </span>
              <h3 className="text-xl md:text-2xl font-bold font-['Manrope'] text-slate-900">
                Upcoming Events
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setEventIndex((i) => (i - 1 + upcomingEvents.length) % upcomingEvents.length)}
                aria-label="Previous event"
                data-testid="footer-events-prev"
                className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:border-[#E08B7A] hover:text-[#E08B7A] text-slate-700 flex items-center justify-center transition"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setEventIndex((i) => (i + 1) % upcomingEvents.length)}
                aria-label="Next event"
                data-testid="footer-events-next"
                className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:border-[#E08B7A] hover:text-[#E08B7A] text-slate-700 flex items-center justify-center transition"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <Link to="/events" className="ml-2 text-sm font-semibold text-[#E08B7A] hover:text-[#D07A69] hidden sm:inline">
                See all
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="footer-events-grid">
            {visibleEvents.map((ev, idx) => (
              <Link
                key={`${ev.title}-${eventIndex}-${idx}`}
                to="/events"
                className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition p-5 flex flex-col gap-3"
                data-testid={`footer-event-card-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                    style={{ backgroundColor: `${ev.color}15`, color: ev.color }}
                  >
                    {ev.tag}
                  </span>
                  <Globe className="w-4 h-4 text-slate-400 group-hover:text-[#E08B7A] transition" />
                </div>
                <h4 className="font-bold text-slate-900 leading-snug group-hover:text-[#E08B7A] transition">
                  {ev.title}
                </h4>
                <div className="text-xs text-slate-500 space-y-1 mt-auto">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ev.color }} />
                    <span>{ev.date}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ev.color }} />
                    <span>{ev.location}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-1.5 mt-5" data-testid="footer-events-dots">
            {upcomingEvents.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setEventIndex(i)}
                aria-label={`Go to event ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === eventIndex ? 'w-6 bg-[#E08B7A]' : 'w-1.5 bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="bg-[#7BC4E2]">
        <div className="container-custom py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 font-['Manrope']">Stay Updated</h3>
              <p className="text-slate-700">Subscribe to our newsletter for the latest news and updates.</p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-2 w-full md:w-auto" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white border-0 rounded-full px-6 min-w-[280px]"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit" 
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6"
                data-testid="newsletter-submit-button"
              >
                <Send className="w-4 h-4 mr-2" />
                Subscribe
              </Button>
            </form>
          </div>
          {subscribeStatus === 'success' && (
            <p className="text-slate-900 mt-2 text-center md:text-right font-medium">Thank you for subscribing!</p>
          )}
          {subscribeStatus === 'already' && (
            <p className="text-slate-900 mt-2 text-center md:text-right">You're already subscribed!</p>
          )}
          {subscribeStatus === 'error' && (
            <p className="text-red-700 mt-2 text-center md:text-right">Something went wrong. Please try again.</p>
          )}
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About Section */}
          <div>
            <img src={LOGO_URL} alt="OPAD Logo" className="h-16 mb-4 bg-white p-2 rounded-lg" />
            <p className="text-slate-400 mb-2">
              Organization for Poverty Alleviation and Development - Empowering communities, reducing poverty, and promoting sustainable development.
            </p>
            <p className="text-sm text-slate-500 mb-4">
              501(c)(3) Nonprofit Organization
            </p>
            <p className="text-[#E08B7A] font-semibold font-['Manrope']">
              Hope in Action. Together We Change Lives.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4 font-['Manrope']">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-slate-400 hover:text-[#7BC4E2] transition-colors">About Us</Link></li>
              <li><Link to="/programs" className="text-slate-400 hover:text-[#7BC4E2] transition-colors">Our Work</Link></li>
              <li><Link to="/where-we-work" className="text-slate-400 hover:text-[#7BC4E2] transition-colors">Where We Work</Link></li>
              <li><Link to="/get-involved" className="text-slate-400 hover:text-[#7BC4E2] transition-colors">Get Involved</Link></li>
              <li><Link to="/media" className="text-slate-400 hover:text-[#7BC4E2] transition-colors">Media & Resources</Link></li>
              <li><Link to="/transparency" className="text-slate-400 hover:text-[#7BC4E2] transition-colors">Transparency</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4 font-['Manrope']">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                <span>OPAD Global Secretariat<br />Stockholm, Sweden</span>
              </li>
              <li className="flex items-center gap-3 text-slate-400">
                <Mail className="w-5 h-5 text-[#7BC4E2] flex-shrink-0" />
                <a href="mailto:contact@opad.eu" className="hover:text-[#7BC4E2] transition-colors">contact@opad.eu</a>
              </li>
              <li className="flex items-center gap-3 text-slate-400">
                <Mail className="w-5 h-5 text-[#E08B7A] flex-shrink-0" />
                <a href="mailto:enquiries@opad.eu" className="hover:text-[#7BC4E2] transition-colors">enquiries@opad.eu</a>
              </li>
            </ul>
          </div>

          {/* Donate & Social */}
          <div>
            <h4 className="text-lg font-bold mb-4 font-['Manrope']">Support Our Mission</h4>
            <a
              href={PAYPAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mb-6"
              data-testid="donate-button-footer"
            >
              <Button className="bg-[#E08B7A] text-white hover:bg-[#D07A69] rounded-full px-8 py-3 font-semibold flex items-center gap-2 animate-pulse-soft">
                <Heart className="w-5 h-5" />
                Donate Now
              </Button>
            </a>
            
            <h4 className="text-lg font-bold mb-4 font-['Manrope']">Follow Us</h4>
            <div className="flex gap-3">
              <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-[#7BC4E2] transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-[#7BC4E2] transition-colors" aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-[#7BC4E2] transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-[#7BC4E2] transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-[#7BC4E2] transition-colors" aria-label="YouTube">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} OPAD. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-500">
              <Link to="/privacy" className="hover:text-[#7BC4E2] transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-[#7BC4E2] transition-colors">Terms & Conditions</Link>
              <Link to="/cookies" className="hover:text-[#7BC4E2] transition-colors">Cookie Policy</Link>
              <Link to="/accessibility" className="hover:text-[#7BC4E2] transition-colors">Accessibility</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
